import Workspace from 'iam/models/Workspace'
import { action, computed, observable, makeObservable } from 'mobx'
import { task } from 'mobx-task'
import InventorySearchViewModel from './InventorySearchViewModel'
import { AlertFactory } from 'alerts/Alert'
import once from 'lodash/once'
import { InventoryTableColumns, ColumnId } from './InventoryTableColumns'
import CreateInventoryItemViewModel from './views/CreateInventoryItemDialog/CreateInventoryItemViewModel'
import InventoryStore from '../InventoryStore'
import ArchiveItemViewModel from './views/ArchiveItemModal/ArchiveItemViewModel'
import EditInventoryItemViewModel from './views/EditInventoryItemDialog/EditInventoryItemViewModel'
import WorkspaceSearchViewModel from './WorkspaceSearchViewModel'
import WorkspaceSelectorViewModel from './WorkspaceSelectorViewModel'
import CreateWorkspaceInventoryItemViewModel from './views/CreateWorkspaceInventoryItemDialog/CreateWorkspaceInventoryItemViewModel'
import OverrideInventoryItemViewModel from './views/OverrideInventoryItemDialog/OverrideInventoryItemViewModel'
import { FilterStorageFactory } from 'infra/FiltersViewModel'
import { GetCurrentUserWorkspaceMemberships } from 'iam/WorkspaceMembershipStore'
import { SupportingItemsPorts } from '../ports'
import UnarchiveItemViewModel from './views/UnarchiveItemModal/UnarchiveItemViewModel'

/**
 * Workspace filter for Inventory Items.
 */
export type InventoryManagerWorkspaceFilter = WorkspaceItems | Global

/**
 * Global Inventory Items
 */
export interface Global {
  type: 'Global'
}

/**
 * Inventory Items in a workspace.
 */
export interface WorkspaceItems {
  type: 'Workspace'
  workspace: Workspace
}

/**
 * View model for the Inventory Manager tab
 */
export default class AdminInventoryManagerViewModel {
  // Uncomment when toggle is removed.
  // @observable columns = InventoryTableColumns
  /**
   * Columns to populate the inventory table header and rows with.
   */
  @computed
  get columns() {
    return InventoryTableColumns
  }

  searcher = new InventorySearchViewModel(
    this.newAlert,
    this.inventoryStore,
    this.getWorkspaceMemberships,
    this.createFilterStorage('InventoryManagement'),
  )

  workspaceSelectorViewModel = new WorkspaceSelectorViewModel(
    this.getWorkspaceMemberships,
  )

  createInventoryItemViewModel = new CreateInventoryItemViewModel(
    this.inventoryStore,
    this.refreshItems.bind(this),
    this.newAlert,
    this.supportingItemsAdapter,
  )

  createWorkspaceInventoryItemViewModel =
    new CreateWorkspaceInventoryItemViewModel(
      this.inventoryStore,
      this.refreshItems.bind(this),
      () => this.searcher.workspace,
      this.workspaceSelectorViewModel,
      this.newAlert,
      this.supportingItemsAdapter,
    )

  editInventoryItemViewModel = new EditInventoryItemViewModel(
    this.inventoryStore,
    this.newAlert,
    this.refreshItems.bind(this),
    () => this.searcher.workspace,
    this.supportingItemsAdapter,
  )

  overrideInventoryItemViewModel = new OverrideInventoryItemViewModel(
    this.inventoryStore,
    this.newAlert,
    this.refreshItems.bind(this),
    () => this.searcher.workspace,
    this.workspaceSelectorViewModel,
    this.supportingItemsAdapter,
  )

  archiveItemViewModel = new ArchiveItemViewModel(
    this.inventoryStore,
    this.refreshItems.bind(this),
    this.newAlert,
    this.selectWorkspaceForFiltering.bind(this),
  )

  unarchiveItemViewModel = new UnarchiveItemViewModel(
    this.inventoryStore,
    this.refreshItems.bind(this),
    this.newAlert,
    this.selectWorkspaceForFiltering.bind(this),
  )

  workspaceSearcher = new WorkspaceSearchViewModel(
    this.newAlert,
    this.inventoryStore,
  )

  /**
   * Ensure we only activate the searcher once, as we want to keep it around
   * for subsequent page views of inventory management.
   */
  private activateSearcher = once(() => this.searcher.activate())

  /**
   * Ensure we only activate the searcher once, as we want to keep it around
   * for subsequent page views of inventory management.
   */
  private activateWorkspaceSearcher = once(() =>
    this.workspaceSearcher.activate(),
  )

  constructor(
    private newAlert: AlertFactory,
    private inventoryStore: InventoryStore,
    private getWorkspaceMemberships: GetCurrentUserWorkspaceMemberships,
    private createFilterStorage: FilterStorageFactory,
    private supportingItemsAdapter: SupportingItemsPorts,
  ) {
    makeObservable(this)
  }

  /**
   * Workspace filter.
   */
  @observable workspaceFilter: InventoryManagerWorkspaceFilter = {
    type: 'Workspace',
    workspace: Workspace.none,
  }

  /**
   * All Workspaces that have inventory items
   */
  @computed
  get workspaces() {
    return this.workspaceSearcher.filteredWorkspaces.map((x) => x.workspace)
  }

  @computed
  get canManageGlobalInventory() {
    return this.getWorkspaceMemberships().some((wms) =>
      wms.member.hasPermission('ADMIN_MANAGE_GLOBAL_INVENTORY_ITEMS'),
    )
  }

  @computed
  get canManageWorkspaceInventory() {
    return this.getWorkspaceMemberships().some((wms) =>
      wms.member.hasPermission('MANAGE_WORKSPACE_INVENTORY_ITEMS'),
    )
  }

  @computed
  get globalInventoryCount() {
    return this.inventoryStore.totalItemCountPerWorkspace.get('global') ?? 0
  }

  @computed
  get globalSelected() {
    return this.workspaceFilter.type === 'Global'
  }

  getWorkspaceInventoryItemsCount(workspace: Workspace) {
    return this.inventoryStore.workspacesWithInventoryItems.get(workspace.id)
      ?.total
  }

  @computed
  get serviceItems() {
    return this.searcher.serviceItems
  }

  @computed
  get canSearch() {
    return this.workspaceSearcher.activeWorkspaces.length >= 5
  }

  @computed
  get workspacesUseDropdown() {
    return this.canManageGlobalInventory
      ? this.canSearch || this.workspaces.length === 1
      : this.workspaces.length > 1
  }

  @action.bound
  addInventoryItem() {
    if (this.workspaceFilter.type === 'Global') {
      return this.createInventoryItemViewModel.show()
    }
    this.createWorkspaceInventoryItemViewModel.show()
  }

  /**
   * Selects the workspace to filter by.
   */
  @action.bound
  selectWorkspaceForFiltering(workspace: Workspace) {
    this.workspaceFilter =
      workspace === Workspace.none ||
      !this.workspaces.find((ws) => ws.id === workspace.id)
        ? { type: 'Global' }
        : { workspace, type: 'Workspace' }

    this.searcher.setWorkspace(
      workspace === Workspace.none ? undefined : workspace,
    )
  }

  async refreshItems(workspace: Workspace) {
    this.selectWorkspaceForFiltering(workspace)
    await this.searcher.refreshItems()
  }

  activate = task.resolved(async () => {
    await this.activateSearcher()
    await this.activateWorkspaceSearcher()
    this.supportingItemsAdapter.fetchDocumentTypes()
    this.workspaceSelectorViewModel.activate()
    if (this.canManageGlobalInventory) {
      this.selectWorkspaceForFiltering(Workspace.none)
    } else if (this.workspaces.length) {
      this.selectWorkspaceForFiltering(this.workspaces[0])
    }
  })

  deactivate() {
    this.workspaceSearcher.deactivate()
    this.workspaceSelectorViewModel.deactivate()
  }
}
